import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spin, Typography } from 'antd';

import ItemLookupSelect from '../common/ItemLookupSelect';
import { countRelatedList, sampleItemOf } from '../../services/relatedLists';
import { describeListState } from '../../utils/relatedLists';
import { normalizeId } from '../../utils/ids';

const { Text } = Typography;

/**
 * "How many would it show?" for one related list, on a sample item.
 *
 * The count is the server's own (`GET /api/items/count` with `related_item`
 * and `related_list`), so it is exactly what the item view will show under
 * the list's default filter: the link, the filter and the caller's read scope
 * all applied. That read takes a SAVED list, so while the draft differs from
 * what is stored in anything the count depends on (source, lookup, filter),
 * the preview says to save first rather than guess.
 *
 * It is started by a press, and its wait and its failure are said in place:
 * a counting line, or a sentence with Try again. The sample is the newest
 * item of the card the caller can read; another can be chosen. A list that
 * cannot find anything is not an error (the API answers 0 with a state), and
 * the state is said in words.
 */

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export default function RelatedListPreview({ listId, listName, readingKey, collectionId, cardId }) {
	const [open, setOpen] = useState(false);
	const [itemId, setItemId] = useState('');
	const [sample, setSample] = useState('idle'); // idle | loading | none | error | ready
	const [run, setRun] = useState({ status: 'idle', count: null, state: null });
	const generation = useRef(0);

	const findSample = useCallback(async () => {
		const mine = ++generation.current;
		setSample('loading');
		try {
			const item = await sampleItemOf(collectionId, cardId);
			if (mine !== generation.current) return;
			if (!item) { setSample('none'); return; }
			setItemId(normalizeId(item._id));
			setSample('ready');
		} catch (error) {
			if (mine === generation.current) setSample('error');
		}
	}, [collectionId, cardId]);

	const count = useCallback(async (forItem) => {
		const mine = ++generation.current;
		setRun({ status: 'loading', count: null, state: null });
		try {
			const result = await countRelatedList(forItem, listId);
			if (mine !== generation.current) return;
			setRun({ status: result.count === null ? 'error' : 'ready', count: result.count, state: result.state });
		} catch (error) {
			if (mine === generation.current) setRun({ status: 'error', count: null, state: null });
		}
	}, [listId]);

	// Count whenever there is a sample and the thing it counts changes.
	useEffect(() => {
		if (!open || !itemId || !readingKey) return;
		count(itemId);
	}, [open, itemId, readingKey, count]);

	const toggle = () => {
		if (open) { setOpen(false); generation.current++; return; }
		setOpen(true);
		if (!itemId) findSample();
	};

	const regionId = `related-list-preview-${listId || 'new'}`;
	const label = `${open ? 'Hide preview of' : 'Preview'} ${listName || 'the list'}`;
	return (
		<div className='related-list__preview'>
			<Button
				type='link'
				size='small'
				aria-expanded={open}
				aria-controls={regionId}
				aria-label={label}
				disabled={!readingKey}
				onClick={toggle}
			>
				{open ? 'Hide preview' : 'Preview'}
			</Button>
			{readingKey ? null : (
				<Text type='secondary' className='related-list__hint'>Save the card to preview this list.</Text>
			)}
			<div id={regionId} className='related-list__preview-body' role='status' aria-live='polite'>
			{open && readingKey ? (
				<>
					{sample === 'loading' ? (
						<span className='related-list__counting'><Spin size='small' /> Finding an item to try it on…</span>
					) : null}
					{sample === 'none' ? (
						<Text type='secondary'>No item of this card to try it on yet. Add one, then preview.</Text>
					) : null}
					{sample === 'error' ? (
						<span>
							<Text type='danger'>Unable to find an item to try it on.</Text>{' '}
							<Button type='link' size='small' onClick={findSample}>Try again</Button>
						</span>
					) : null}
					{sample === 'ready' ? (
						<div className='related-list__sample'>
							<span className='related-list__sample-label'>Try it on</span>
							<ItemLookupSelect
								collectionId={collectionId}
								cardId={cardId}
								value={itemId}
								allowClear={false}
								onChange={(next) => { if (next) setItemId(normalizeId(next)); }}
								ariaLabel='Sample item'
								placeholder='Pick an item'
							/>
						</div>
					) : null}
					{sample === 'ready' && run.status === 'loading' ? (
						<span className='related-list__counting'><Spin size='small' /> Counting…</span>
					) : null}
					{sample === 'ready' && run.status === 'ready' ? (
						<div>
							<strong>{plural(run.count, 'item', 'items')}</strong>
							<Text type='secondary'> would show under this list for that item.</Text>
							{run.state ? <div><Text type='warning'>{describeListState(run.state)}</Text></div> : null}
						</div>
					) : null}
					{sample === 'ready' && run.status === 'error' ? (
						<span>
							<Text type='danger'>Unable to count this list.</Text>{' '}
							<Button type='link' size='small' onClick={() => count(itemId)}>Try again</Button>
						</span>
					) : null}
				</>
			) : null}
			</div>
		</div>
	);
}
